import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse, HubspotContact, HubspotDeal } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';

/** One event in a contact's unified HubSpot activity timeline. */
export interface HubspotActivityEvent {
  type: string;
  timestamp: string;
  title: string;
  detail?: string;
  sourceId?: string;
  raw?: {
    url?: string;
    [key: string]: unknown;
  };
}

export interface HubspotActivityTimeline {
  contactId: string;
  events: HubspotActivityEvent[];
  warnings?: string[];
}

export const crmApi = createApi({
  reducerPath: 'crmApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getContactActivityTimeline: builder.query<
      ApiResponse<HubspotActivityTimeline>,
      {
        contactIdOrEmail: string;
        after?: string;
        before?: string;
        types?: string;
      }
    >({
      query: ({ contactIdOrEmail, after, before, types }) => ({
        url: `/v1/contacts/${encodeURIComponent(contactIdOrEmail)}/activity-timeline`,
        params: {
          ...(after ? { after } : {}),
          ...(before ? { before } : {}),
          ...(types ? { types } : {}),
        },
      }),
    }),
    getDealDetails: builder.query<ApiResponse<HubspotDeal>, string>({
      query: (dealId) => `/v1/crm/hubspot/deal/${dealId}`,
    }),
    getPipelines: builder.query<ApiResponse<any>, string>({
      query: (objectType) => `/v1/crm/hubspot/pipeline/${objectType}`,
    }),
    getHubspotFactFindDetail: builder.query<
      ApiResponse<HubspotContact>,
      string
    >({
      query: (contactId) => {
        return {
          url: `/v1/crm/hubspot/contact/${contactId}/factfind`,
        };
      },
    }),
    getHubspotFactFindDetailByEmail: builder.query<
      ApiResponse<HubspotContact>,
      string
    >({
      query: (email) => ({
        url: '/v1/crm/hubspot/contact/factfind/by-email',
        params: { email },
      }),
    }),
    getHubspotContacts: builder.query<
      ApiResponse<any>,
      { after?: string | null; limit?: number }
    >({
      query: ({ after, limit = 10 }) => {
        const params = new URLSearchParams();
        if (after) {
          params.append('after', after);
        }
        params.append('limit', limit.toString());
        return {
          url: `/v1/crm/hubspot/contact/list?${params.toString()}`,
        };
      },
    }),
    getHubspotContactDetail: builder.query<ApiResponse<HubspotContact>, string>(
      {
        query: (contactId) => {
          return {
            url: `/v1/crm/hubspot/contact/${contactId}`,
          };
        },
      },
    ),
    searchHubspotContact: builder.mutation<
      ApiResponse<{ total: number; results: HubspotContact[] }>,
      { email: string }
    >({
      query: (body) => {
        return {
          url: '/v1/crm/hubspot/contact/search',
          method: 'POST',
          body,
        };
      },
    }),
    getHubspotDealByContactValue: builder.mutation<
      ApiResponse<any>,
      { value: string }
    >({
      query: ({ value }) => {
        return {
          url: `/v1/crm/hubspot/deals/contact`,
          method: 'POST',
          body: { value },
        };
      },
    }),
    getHubspotNotesByDealId: builder.query<ApiResponse<any>, string>({
      query: (dealId) => {
        return {
          url: `/v1/crm/hubspot/deal/${dealId}/notes`,
        };
      },
    }),
  }),
});

export const {
  useGetHubspotContactsQuery,
  useGetPipelinesQuery,
  useGetDealDetailsQuery,
  useGetHubspotFactFindDetailQuery,
  useGetHubspotFactFindDetailByEmailQuery,
  useGetHubspotContactDetailQuery,
  useSearchHubspotContactMutation,
  useGetHubspotDealByContactValueMutation,
  useGetHubspotNotesByDealIdQuery,
  useGetContactActivityTimelineQuery,
} = crmApi;
